import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useSelection } from '../context/SelectionContext';
import { toast } from 'sonner';
import { X, ChevronRight, Zap, ArrowDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEXTS = {
  es: { accept: 'Quiero esto', decline: 'No, gracias', downsellDecline: 'Seguir con mi pedido' },
  en: { accept: 'I want this', decline: 'No, thanks', downsellDecline: 'Continue with my order' },
  cat: { accept: 'Vull això', decline: 'No, gràcies', downsellDecline: 'Seguir amb la comanda' },
  fr: { accept: 'Je veux ça', decline: 'Non, merci', downsellDecline: 'Continuer ma commande' },
};

const UpsellAddPopup = ({ triggerProduct, type = 'upsell', onAccept, onDecline }) => {
  const { language, localize } = useLanguage();
  const { addItem } = useSelection();
  const [targetProduct, setTargetProduct] = useState(null);
  const [visible, setVisible] = useState(false);
  const t = TEXTS[language] || TEXTS.es;

  const productId = type === 'upsell'
    ? triggerProduct?.upsell_product_id
    : triggerProduct?.downsell_product_id;

  const rawTitle = type === 'upsell'
    ? triggerProduct?.upsell_title
    : triggerProduct?.downsell_title;

  const rawDescription = type === 'upsell'
    ? triggerProduct?.upsell_description
    : triggerProduct?.downsell_description;

  useEffect(() => {
    if (!productId) return;
    const fetchProduct = async () => {
      try {
        const res = await fetch(`${API_URL}/api/products/${productId}`);
        if (res.ok) {
          const data = await res.json();
          setTargetProduct(data);
          setVisible(true);
        }
      } catch (e) { /* silent */ }
    };
    fetchProduct();
  }, [productId]);

  const loc = (field) => {
    if (!field) return '';
    return field[language] || field.es || '';
  };

  const handleAccept = () => {
    if (!targetProduct) return;
    addItem({
      id: targetProduct.id,
      name: localize(targetProduct.name),
      price: targetProduct.price,
      image_url: targetProduct.images?.[0] || targetProduct.image_url,
      quantity: 1,
      options: []
    });
    toast.success(`+ ${localize(targetProduct.name)}`, { duration: 1200 });
    setVisible(false);
    onAccept?.();
  };

  const handleDecline = () => {
    setVisible(false);
    onDecline?.();
  };

  if (!visible || !targetProduct) return null;

  const title = loc(rawTitle) || localize(targetProduct.name);
  const description = loc(rawDescription) || '';
  const image = targetProduct.images?.[0] || targetProduct.image_url;
  const isDownsell = type === 'downsell';

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      style={{ animation: 'fadeIn 0.2s ease' }}
      data-testid={`upsell-add-popup-${type}`}
    >
      <div
        className="w-full max-w-sm overflow-hidden rounded-2xl shadow-2xl"
        style={{
          backgroundColor: '#FBF5E8',
          animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Image */}
        {image && (
          <div className="relative h-44 overflow-hidden">
            <img src={image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            <button
              onClick={handleDecline}
              className="absolute top-3 right-3 p-2 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors"
              data-testid={`upsell-add-close-${type}`}
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="p-5 text-center">
          {/* Badge */}
          <div className="flex items-center justify-center gap-2 mb-2">
            {isDownsell
              ? <ArrowDown size={16} style={{ color: '#5C9E6F' }} />
              : <Zap size={16} style={{ color: '#B78C3C' }} />
            }
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: isDownsell ? '#5C9E6F' : '#B78C3C' }}>
              {isDownsell ? (language === 'es' ? 'Te puede interesar' : 'You might like') : (language === 'es' ? 'Sube de nivel' : 'Level up')}
            </span>
          </div>

          <h3 className="text-xl font-serif mb-1" style={{ color: '#2C2C2A' }}>
            {title}
          </h3>
          {description && (
            <p className="text-sm mb-3" style={{ color: '#5C5C59' }}>{description}</p>
          )}

          {/* Experience items preview */}
          {targetProduct.experience_items?.length > 0 && (
            <div className="text-left mb-4 space-y-1 px-2">
              {targetProduct.experience_items.slice(0, 4).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#5C5C59' }}>
                  <span style={{ color: '#B78C3C' }}>&#10003;</span>
                  {loc(item)}
                </div>
              ))}
            </div>
          )}

          {/* CTA Accept */}
          <button
            onClick={handleAccept}
            className="w-full py-3.5 rounded-xl text-white font-semibold text-base shadow-lg active:scale-[0.97] transition-transform flex items-center justify-center gap-2"
            style={{
              background: isDownsell
                ? 'linear-gradient(135deg, #5C9E6F 0%, #6FB07F 50%, #5C9E6F 100%)'
                : 'linear-gradient(135deg, #B78C3C 0%, #D4AF37 50%, #C9A84C 100%)',
            }}
            data-testid={`upsell-add-accept-${type}`}
          >
            {localize(targetProduct.name)} — {targetProduct.price?.toFixed(2)}€
            <ChevronRight size={18} />
          </button>

          {/* CTA Decline */}
          <button
            onClick={handleDecline}
            className="w-full mt-2 py-2.5 rounded-xl text-sm font-medium"
            style={{ color: '#8C8C89' }}
            data-testid={`upsell-add-decline-${type}`}
          >
            {isDownsell ? t.downsellDecline : t.decline}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpsellAddPopup;
